import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { PagesPaginateModel } from "../../models/Paginate/PagesPaginate";
import { ToastSettings } from "../../utils/ToastSettings";
import { ArticlesService } from "../../services/Articles/ArticlesService";
import { ArticlesModel } from "../../models/Articles/ArticleModel";
import { VersionModel } from "../../models/Articles/VersionModel";

export interface ArticleBulkReplaceFileResult {
  replaced_ids: number[];
  skipped: { id: number; reason: string }[];
  not_found_ids: number[];
}

interface ContextProps {
  article: ArticlesModel | null;
  setArticle: Dispatch<SetStateAction<ArticlesModel | null>>;
  articles: ArticlesModel[];
  sortArticles: ArticlesModel[];
  articlesRelated: ArticlesModel[];
  articlesRelatedOptions: any[];
  articleVersion: VersionModel[];
  setArticlesRelatedOptions: Dispatch<SetStateAction<any[]>>;
  articlesStructure: any;
  articleVote: any;
  articlesMostRead: any[];
  params: any;
  paramsSortArticle: any;
  setParams: Dispatch<SetStateAction<any>>;
  setParamsSortArticle: Dispatch<SetStateAction<any>>;
  paramsArticlesRelated: any;
  setParamsArticlesRelated: Dispatch<SetStateAction<any>>;
  pages: PagesPaginateModel;
  setPages: Dispatch<SetStateAction<PagesPaginateModel>>;
  isLoading: boolean;
  isLoadingVote: boolean;
  isLoadingMostRead: boolean;
  isLoadingSortArticle: boolean;
  isLoadingVersion: boolean;
  isLoadingRestoreVersion: boolean;
  isPreview: boolean;
  isPreviewExcel: boolean;
  error: string;
  setError: Dispatch<SetStateAction<string>>;
  getLastGetStatusCode: () => number | null;
  setSortArticles: Dispatch<SetStateAction<any>>;
  handleList: () => void;
  handleListSortArticle: (paramsSortArticle: any) => void;
  handleFetch: (id: number | null, data: any) => Promise<boolean>;
  handleDelete: (id: number) => Promise<boolean>;
  handleDeactivateBulk: (ids: number[]) => Promise<boolean>;
  handleReplaceFileBulk: (ids: number[], file: File) => Promise<ArticleBulkReplaceFileResult | null>;
  handleGet: (id: number) => Promise<ArticlesModel | null>;
  handleListByStructure: (
    params: any,
    viewMode: "category" | "section" | "subsection"
  ) => Promise<boolean>;
  handleListArticlesRelated: () => void;
  handleVote: (articleId: number, vote: boolean) => void;
  handleGetVoteStatus: (articleId: number) => void;
  handleGetMostRead: () => void;
  handleInsertView: (articleId: number) => void;
  handleSortArticle: (params: any) => Promise<boolean>;
  handleListVersion: (params: any) => void;
  handleRestoreVersion: (params: any) => Promise<boolean>;
  setIsPreview: Dispatch<SetStateAction<boolean>>;
  setIsPreviewExcel: Dispatch<SetStateAction<boolean>>;
  setIsLoadingVersion: Dispatch<SetStateAction<boolean>>;
  handleReactivate: (id: number) => Promise<boolean>;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
}

export const ArticlesContext = createContext<ContextProps>({} as ContextProps);

interface ArticlesProviderProps {
  children: ReactNode;
}

export const ArticlesProvider = ({ children }: ArticlesProviderProps) => {
  const [article, setArticle] = useState<ArticlesModel | null>(null);
  const [articles, setArticles] = useState<ArticlesModel[]>([]);
  const [sortArticles, setSortArticles] = useState<ArticlesModel[]>([]);
  const [articlesRelated, setArticlesRelated] = useState<ArticlesModel[]>([]);
  const [articleVersion, setArticleVersion] = useState<VersionModel[]>([]);
  const [articlesRelatedOptions, setArticlesRelatedOptions] = useState<
    any | null
  >(null);
  const [articlesStructure, setArticlesStructure] = useState<any>(null);
  const [articleVote, setArticleVote] = useState<any | null>(null);
  const [articlesMostRead, setArticlesMostRead] = useState<any[]>([]);
  const [params, setParams] = useState<any>(null);
  const [paramsSortArticle, setParamsSortArticle] = useState<any>(null);
  const [paramsArticlesRelated, setParamsArticlesRelated] = useState<any>(null);
  const [pages, setPages] = useState<PagesPaginateModel>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingVote, setIsLoadingVote] = useState<boolean>(false);
  const [isPreview, setIsPreview] = useState<boolean>(false);
  const [isPreviewExcel, setIsPreviewExcel] = useState<boolean>(false);
  const [isLoadingMostRead, setIsLoadingMostRead] = useState<boolean>(false);
  const [isLoadingSortArticle, setIsLoadingSortArticle] =
    useState<boolean>(false);
  const [isLoadingVersion, setIsLoadingVersion] = useState<boolean>(false);
  const [isLoadingRestoreVersion, setIsLoadingRestoreVersion] =
    useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const lastGetStatusCodeRef = useRef<number | null>(null);

  const service = new ArticlesService();
  const location = useLocation();

  const handleList = useCallback(async () => {
    if (!params || !location.pathname.includes("/articles/list")) {
      return false;
    }

    try {
      setIsLoading(true);
      const [_Response, _Error] = await service.list(params);
      setIsLoading(false);

      if (!!_Error) {
        return setError(_Response?.message || _Error);
      }

      setArticles(_Response?.items);
      setPages(_Response?.pagination);
      return setError("");
    } catch (e) {
      setIsLoading(false);
      setError("Não foi possível carregar os artigos do sistema.");
    }
  }, [params, location.pathname]);

  const handleListSortArticle = useCallback(async (paramsSortArticle: any) => {
    try {
      setIsLoadingSortArticle(true);
      const [_Response, _Error] = await service.list(paramsSortArticle);
      setIsLoadingSortArticle(false);

      if (!!_Error) {
        return setError(_Response?.message || _Error);
      }

      setSortArticles(_Response?.items);
      setPages(_Response?.pagination);
      return setError("");
    } catch (e) {
      setIsLoadingSortArticle(false);
      setError("Não foi possível carregar os artigos do sistema.");
    }
  }, []);

  const handleListArticlesRelated = useCallback(async () => {
    if (!paramsArticlesRelated) {
      return false;
    }

    try {
      const [_Response, _Error] = await service.list(paramsArticlesRelated);

      if (!!_Error) {
        return setError(_Response?.message || _Error);
      }

      setArticlesRelated(_Response?.items);
      return setError("");
    } catch (e) {
      setError("Não foi possível carregar os artigos do sistema.");
    }
  }, [paramsArticlesRelated]);

  const handleFetch = async (id: number | null, data: any) => {
    try {
      const [_Response, _Error, _Code, _Errors] = await service.fetch(id, data);
      if (!!_Error) {
        ToastSettings(
          _Response?.message || _Error,
          "bottom-center",
          "error",
          () => {},
          _Errors
        );
        return false;
      }

      setError("");
      return true;
    } catch (e) {
      setError("Houve um erro ao salvar o artigo");
      return false;
    }
  };

  const handleGet = async (id: number): Promise<ArticlesModel | null> => {
    try {
      setIsLoading(true);
      lastGetStatusCodeRef.current = null;
      const [_Response, _Error, _Code] = await service.list(
        {
          filter: { id: id },
          withPaginate: false,
        },
        { skipAuthRedirect: true }
      );
      setIsLoading(false);

      if (!!_Error) {
        lastGetStatusCodeRef.current = _Code || null;
        setError(_Response?.message || _Error);
        return null;
      }

      setError("");
      return _Response?.items[0] || null;
    } catch (e: any) {
      setIsLoading(false);
      lastGetStatusCodeRef.current = e.response?.status || null;
      setError(e.response?.data?.message || "Houve um erro ao buscar o artigo");
      return null;
    }
  };

  const handleDelete = async (id: number) => {
    try {
      setIsLoading(true);
      const [_Response, _Error] = await service.delete(id);
      setIsLoading(false);

      if (!!_Error) {
        setError(_Response?.message || _Error);
        return false;
      }

      setError("");
      return true;
    } catch (e: any) {
      setIsLoading(false);
      setError(
        e.response?.data?.message || "Houve um erro ao deletar o artigo"
      );
      return false;
    }
  };

  const handleDeactivateBulk = async (ids: number[]) => {
    try {
      setIsLoading(true);
      const [_Response, _Error] = await service.deactivateBulk(ids);
      setIsLoading(false);

      if (!!_Error) {
        setError(_Response?.message || _Error);
        return false;
      }

      setError("");
      return true;
    } catch (e: any) {
      setIsLoading(false);
      setError(
        e.response?.data?.message ||
          "Houve um erro ao inativar os artigos selecionados"
      );
      return false;
    }
  };

  const handleReplaceFileBulk = async (ids: number[], file: File): Promise<ArticleBulkReplaceFileResult | null> => {
    try {
      setIsLoading(true);
      const [_Response, _Error] = await service.replaceFileBulk(ids, file);
      setIsLoading(false);

      if (!!_Error) {
        setError(_Response?.message || _Error);
        return null;
      }

      setError("");
      return _Response?.data ?? null;
    } catch (e: any) {
      setIsLoading(false);
      setError(
        e.response?.data?.message ||
          "Houve um erro ao substituir os anexos dos artigos selecionados"
      );
      return null;
    }
  };

  const handleListByStructure = async (
    params: any,
    viewMode: "category" | "section" | "subsection"
  ) => {
    try {
      setIsLoading(true);
      const [_Response, _Error] = await service.listByStructure({
        filter: params,
      });
      setIsLoading(false);

      if (!!_Error) {
        setError(_Response?.message || _Error);
        return false;
      }

      setError("");
      setArticlesStructure(
        viewMode === "category"
          ? _Response?.data?.items[0] || null
          : _Response?.data?.items || null
      );
      return true;
    } catch (e: any) {
      setIsLoading(false);
      setError(
        e.response?.data?.message || "Houve um erro ao buscar os artigos"
      );
      return false;
    }
  };

  const handleVote = async (articleId: number, vote: boolean) => {
    try {
      setIsLoadingVote(true);

      const [_Response, _Error, _Code, _Errors] = await service.vote(
        articleId,
        vote
      );

      if (!!_Error) {
        ToastSettings(
          _Response?.message || _Error,
          "bottom-center",
          "error",
          () => {},
          _Errors
        );
      }

      handleGetVoteStatus(articleId);

      setIsLoadingVote(false);
    } catch (e) {
      setError("Houve um erro ao registrar seu voto.");
    }
  };

  const handleGetVoteStatus = async (articleId: number) => {
    try {
      setIsLoadingVote(true);

      const [_Response, _Error, _Code, _Errors] = await service.voteStatus(
        articleId
      );

      if (!!_Error) {
        ToastSettings(
          _Response?.message || _Error,
          "bottom-center",
          "error",
          () => {},
          _Errors
        );
      }

      setArticleVote(_Response?.data?.items || null);

      setIsLoadingVote(false);
    } catch (e) {
      setError("Houve um erro ao registrar seu voto.");
    }
  };

  const handleGetMostRead = async () => {
    try {
      setIsLoadingMostRead(true);

      const [_Response, _Error, _Code, _Errors] = await service.mostRead();

      if (!!_Error) {
        ToastSettings(
          _Response?.message || _Error,
          "bottom-center",
          "error",
          () => {},
          _Errors
        );
      }

      setArticlesMostRead(_Response?.data || null);

      setIsLoadingMostRead(false);
    } catch (e) {
      setError("Houve um erro ao buscar os artigos mais lidos.");
    }
  };

  const handleInsertView = async (articleId: number) => {
    try {
      const [_Response, _Error, _Code, _Errors] = await service.insertView(
        articleId
      );

      if (!!_Error) {
        ToastSettings(
          _Response?.message || _Error,
          "bottom-center",
          "error",
          () => {},
          _Errors
        );
      }

      return true;
    } catch (e) {
      setError("Houve um erro ao registrar a visualização.");
    }
  };

  const handleSortArticle = async (params: any) => {
    try {
      const [_Response, _Error, _Code, _Errors] = await service.sortArticle(
        params
      );

      if (!!_Error) {
        ToastSettings(
          _Response?.message || _Error,
          "bottom-center",
          "error",
          () => {},
          _Errors
        );
        return false;
      }

      return true;
    } catch (e) {
      setError("Houve um erro ao ordernar artigo.");
      return false;
    }
  };

  const handleListVersion = useCallback(async (params: any) => {
    try {
      setIsLoadingVersion(true);
      const [_Response, _Error] = await service.version(params);
      setIsLoadingVersion(false);

      if (!!_Error) {
        return setError(_Response?.message || _Error);
      }

      setArticleVersion(_Response?.data?.items);
      return setError("");
    } catch (e) {
      setIsLoadingVersion(false);
      setError("Não foi possível carregar as versões dos artigos.");
    }
  }, []);

  const handleRestoreVersion = useCallback(async (params: any) => {
    try {
      setIsLoadingRestoreVersion(true);
      const [_Response, _Error] = await service.restoreVersion(params);
      setIsLoadingRestoreVersion(false);

      if (!!_Error) {
        setError(_Response?.message || _Error);
        return false;
      }

      setError("");
      return true;
    } catch (e) {
      setIsLoadingRestoreVersion(false);
      setError("Não foi possível restaurar a versão do artigo.");
      return false;
    }
  }, []);

  const handleReactivate = async (id: number) => {
    try {
      setIsLoading(true);
      const [_Response, _Error] = await service.reactivate(id);
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);

      if (!!_Error) {
        setError(_Response?.message || _Error);
        return false;
      }

      setError("");
      return true;
    } catch (e: any) {
      setIsLoading(false);
      setError(
        e.response?.data?.message || "Houve um erro ao reativar a artigo"
      );
      return false;
    }
  };

  useEffect(() => {
    !!error && toast.error(error);
  }, [error]);

  useEffect(() => {
    handleList().then();
  }, [handleList]);

  useEffect(() => {
    handleListArticlesRelated().then();
  }, [handleListArticlesRelated]);

  useEffect(() => {
    setArticlesRelatedOptions(
      articlesRelated?.map((item: ArticlesModel) => ({
        label: item.category?.name + " - " + item.title,
        value: item.id,
        category: item.category,
      }))
    );
  }, [articlesRelated]);

  return (
    <ArticlesContext.Provider
      value={{
        article,
        setArticle,
        articles,
        articlesRelated,
        setArticlesRelatedOptions,
        articlesRelatedOptions,
        articlesStructure,
        articleVote,
        articlesMostRead,
        articleVersion,
        params,
        setParams,
        paramsArticlesRelated,
        setParamsArticlesRelated,
        paramsSortArticle,
        setParamsSortArticle,
        pages,
        setPages,
        setSortArticles,
        isLoading,
        isLoadingVote,
        isLoadingMostRead,
        isPreview,
        isPreviewExcel,
        error,
        getLastGetStatusCode: () => lastGetStatusCodeRef.current,
        sortArticles,
        isLoadingSortArticle,
        isLoadingVersion,
        isLoadingRestoreVersion,
        setError,
        handleList,
        handleFetch,
        handleDelete,
        handleDeactivateBulk,
        handleReplaceFileBulk,
        handleGet,
        handleListByStructure,
        handleListArticlesRelated,
        handleVote,
        handleGetVoteStatus,
        handleGetMostRead,
        handleInsertView,
        handleListSortArticle,
        handleSortArticle,
        handleListVersion,
        handleRestoreVersion,
        setIsPreview,
        setIsPreviewExcel,
        setIsLoadingVersion,
        setIsLoading,
        handleReactivate,
      }}
    >
      {children}
    </ArticlesContext.Provider>
  );
};

export const useArticles = () => useContext(ArticlesContext);
